import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, Check } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const PLAN_LABELS = {
  starter: 'Starter',
  business: 'Business',
  pro: 'Pro',
};

function formatEuro(value) {
  return Number(value ?? 0).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function UpsellingBadge({ status }) {
  if (status === 'Inviata Business') {
    return (
      <Badge
        className="text-xs font-medium border-0"
        style={{ backgroundColor: 'rgba(212, 175, 55, 0.18)', color: '#D4AF37' }}
      >
        {status}
      </Badge>
    );
  }
  if (status === 'Inviata Pro') {
    return (
      <Badge
        className="text-xs font-medium border-0"
        style={{ backgroundColor: 'rgba(96, 165, 250, 0.18)', color: '#60A5FA' }}
      >
        {status}
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-xs font-normal"
      style={{ borderColor: 'rgba(255,255,255,0.12)', color: '#64748B' }}
    >
      Nessuna
    </Badge>
  );
}

function buildResocontoCopyText(row) {
  const planLabel = PLAN_LABELS[row.subscription_plan] || row.subscription_plan;
  return [
    `${row.name || 'Studio'} - Piano: ${planLabel}.`,
    `Lead ricevuti: ${row.leads_received} (extra: ${row.leads_extra}).`,
    `Imponibile: ${formatEuro(row.imponibile)} €, IVA: ${formatEuro(row.iva)} €, Totale Lordo: ${formatEuro(row.totale_lordo)} €`,
  ].join(' ');
}

function CopyResocontoButton({ row }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = buildResocontoCopyText(row);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      onClick={handleCopy}
      className="text-xs gap-1.5"
      style={{ color: copied ? '#22C55E' : '#D4AF37' }}
      data-testid={`copy-resoconto-${row.lawyer_id}`}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copiato' : 'Copia Resoconto'}
    </Button>
  );
}

export function AdminFinancialSummaryPanel({ data, isLoading, generatedAt }) {
  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="spinner" />
      </div>
    );
  }

  const items = data?.items || [];
  const periodLabel = data?.period_label || '—';
  const grandTotal = data?.grand_total_lordo ?? 0;
  const updatedLabel = generatedAt
    ? new Date(generatedAt).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
    : '—';

  return (
    <div className="space-y-4" data-testid="admin-financial-summary-panel">
      <div
        className="rounded-xl p-5 md:p-6"
        style={{
          backgroundColor: '#112240',
          border: '1px solid rgba(212, 175, 55, 0.35)',
        }}
      >
        <p className="text-xs uppercase tracking-wide" style={{ color: '#A0ABC0' }}>
          Fatturato Totale Lordo Complessivo — {periodLabel}
        </p>
        <p
          className="text-3xl md:text-4xl font-semibold mt-2"
          style={{ color: '#D4AF37', fontFamily: 'Playfair Display, serif' }}
        >
          {formatEuro(grandTotal)} €
        </p>
        <p className="text-xs mt-2" style={{ color: '#64748B' }}>
          Somma dei totali lordi per avvocato (canone + lead extra + oneri fiscali). Solo assegnazioni automatiche.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs" style={{ color: '#64748B' }}>
          Dati aggiornati: {updatedLabel}
        </p>
        <p className="text-xs" style={{ color: '#64748B' }}>
          Periodo: mese corrente
        </p>
      </div>

      <div
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: '#112240', border: '1px solid rgba(96, 165, 250, 0.22)' }}
      >
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                <TableHead style={{ color: '#A0ABC0' }}>Avvocato</TableHead>
                <TableHead style={{ color: '#A0ABC0' }}>Piano</TableHead>
                <TableHead className="text-right" style={{ color: '#A0ABC0' }}>Lead</TableHead>
                <TableHead className="text-right" style={{ color: '#A0ABC0' }}>Extra</TableHead>
                <TableHead className="text-right" style={{ color: '#A0ABC0' }}>Canone</TableHead>
                <TableHead className="text-right" style={{ color: '#A0ABC0' }}>Quota extra</TableHead>
                <TableHead className="text-right" style={{ color: '#A0ABC0' }}>Imponibile</TableHead>
                <TableHead className="text-right" style={{ color: '#A0ABC0' }}>Cassa 4%</TableHead>
                <TableHead className="text-right" style={{ color: '#A0ABC0' }}>IVA 22%</TableHead>
                <TableHead className="text-right" style={{ color: '#A0ABC0' }}>Ritenuta 20%</TableHead>
                <TableHead className="text-right" style={{ color: '#A0ABC0' }}>Totale lordo</TableHead>
                <TableHead style={{ color: '#A0ABC0' }}>Upselling</TableHead>
                <TableHead style={{ color: '#A0ABC0' }}>Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                  <TableCell colSpan={13} className="text-center py-10" style={{ color: '#64748B' }}>
                    Nessun avvocato registrato o nessun dato nel periodo.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow
                    key={row.lawyer_id}
                    style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm" style={{ color: '#F8F9FA' }}>
                          {row.name || '—'}
                        </p>
                        <p className="text-xs" style={{ color: '#64748B' }}>
                          {row.email}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell style={{ color: '#F8F9FA' }}>
                      {PLAN_LABELS[row.subscription_plan] || row.subscription_plan}
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#F8F9FA' }}>
                      {row.leads_received}
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#F8F9FA' }}>
                      {row.leads_extra}
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#A0ABC0' }}>
                      {formatEuro(row.monthly_fee)} €
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#60A5FA' }}>
                      {formatEuro(row.quota_extra)} €
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#F8F9FA' }}>
                      {formatEuro(row.imponibile)} €
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#A0ABC0' }}>
                      {formatEuro(row.cassa_previdenziale)} €
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#A0ABC0' }}>
                      {formatEuro(row.iva)} €
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#A0ABC0' }}>
                      {formatEuro(row.ritenuta_acconto)} €
                    </TableCell>
                    <TableCell className="text-right font-semibold" style={{ color: '#D4AF37' }}>
                      {formatEuro(row.totale_lordo)} €
                    </TableCell>
                    <TableCell>
                      <UpsellingBadge status={row.upselling_status} />
                    </TableCell>
                    <TableCell>
                      <CopyResocontoButton row={row} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
